import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal } from "@legal-500/legal500-design-system";
import { awards as awardsService } from "../../../api/awards.service.js";
import { contacts as contactsService, type Contact } from "../../../api/contacts.service.js";
import styles from "./LinkLawyerModal.module.css";

/**
 * Same two constants as LinkFirmModal, and deliberately the same values: the two
 * modals are one repair job done twice, and a lawyer search that debounced at a
 * different rhythm than the firm search directly above it would be noticeable
 * for no reason.
 *
 * The cap still earns its keep here even though every result set is one firm's
 * roster: a magic-circle firm has four figures of contacts in the Address Book,
 * so an empty search box against one of those is exactly the request the
 * truncation line below exists to describe.
 */
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;

type LinkLawyerModalProps = {
  awardId: string;
  /** Verbatim lawyer text from the source spreadsheet. Seeds the search *and*
   * stays on screen in its own right — see the header line below. */
  lawyerNameRaw: string;
  /**
   * The Address Book company this award is already linked to. **Not nullable**,
   * unlike `Award["companyId"]`: the candidate list is scoped to it, so an award
   * without one has no modal to open at all. The page enforces that by only
   * mounting this component for a row that has a company, and the cell enforces
   * it by not offering the button — this type is the third statement of the same
   * rule, in the one place a compiler can check it.
   */
  companyId: string;
  /** Resolved firm name, for the scope line. Null is possible in the contract
   * (`companyId` is what means "matched"; the name only follows from it), so the
   * line falls back rather than printing "at null". */
  firmName: string | null;
  onClose: () => void;
  onLinked: () => void;
};

/**
 * Links an award whose lawyer the importer could not match to an Address Book
 * contact.
 *
 * The sibling of LinkFirmModal, and mirrors it closely on purpose — seeded
 * search, debounce, stale-response guard, a table of candidates with one "Link"
 * button per row, failures reported inside the modal, no toast (staff-ui has no
 * toast mechanism; the list refetching underneath is the confirmation).
 *
 * The one real difference is the scope. Candidates come from the award's
 * already-linked firm, never from the whole Address Book, because that is the
 * only search that can be checked by the person doing the checking: `GET
 * /v1/contacts` returns no company *name*, so a global result list would offer
 * six people called "M. Cole" with nothing to tell them apart. awards-api's
 * matcher draws the same line — every name-based lawyer tier opens with
 * `if (firmMatch.status !== "matched" || !firmMatch.companyId) return null`
 * (awards-matcher.ts:1293, 1404) — so this modal is doing by hand exactly what
 * the importer does automatically, on the rows where it gave up.
 */
export function LinkLawyerModal({
  awardId,
  lawyerNameRaw,
  companyId,
  firmName,
  onClose,
  onLinked,
}: LinkLawyerModalProps) {
  const [searchTerm, setSearchTerm] = useState(lawyerNameRaw);
  const [results, setResults] = useState<Contact[]>([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const initialSearchDone = useRef(false);

  const search = useCallback(
    async (term: string) => {
      const trimmed = term.trim();

      // An empty box IS a search here, which is where this parts company with
      // LinkFirmModal. There, `name=` would return the first 20 companies in the
      // Address Book — an arbitrary list with nothing to do with the award. Here
      // `companyId` alone returns this firm's roster: short, browsable, and
      // precisely what you want when the spreadsheet's spelling of the name is
      // too mangled to search with ("J. Vaile (Corporate)").
      const requestId = ++requestIdRef.current;
      setSearching(true);
      setError(null);

      try {
        const response = await contactsService.getAll({
          companyId,
          search: trimmed || undefined,
          limit: SEARCH_LIMIT,
        });
        // A slower earlier request must not overwrite a faster later one: the box
        // is edited freely and responses arrive in whatever order the server
        // manages, so only the newest request is allowed to write state.
        if (requestId !== requestIdRef.current) return;
        setResults(response.contacts);
        setTotalMatches(response.pagination.total);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setResults([]);
        setTotalMatches(0);
        setError("Couldn't search the Address Book. Try again.");
      } finally {
        if (requestId === requestIdRef.current) setSearching(false);
      }
    },
    [companyId],
  );

  // One search on open, undebounced: the seeded term is already on screen, so
  // making the user wait 300ms to see what it matches would be waiting for
  // nothing. The ref guard is LinkContactModal's — StrictMode double-invokes
  // effects in development and this must stay one request.
  useEffect(() => {
    if (initialSearchDone.current) return;
    initialSearchDone.current = true;
    search(lawyerNameRaw);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  function handleSearchChange(next: string) {
    setSearchTerm(next);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      search(next);
    }, SEARCH_DEBOUNCE_MS);
  }

  async function handleLink(contact: Contact) {
    setLinkingId(contact.id);
    setError(null);

    try {
      // `contact.id`, the Address Book uuid — NOT `contact.contactId`, which is
      // the legacy WordPress term id. Both are called "the contact id" in
      // conversation, the award row stores the uuid, and the legacy integer is
      // what `contacts.getByName` would have handed back, which is why this
      // modal searches with `getAll` instead.
      await awardsService.match(awardId, { contactId: contact.id });
      onLinked();
    } catch {
      setError("Couldn't link this contact. Try again.");
      setLinkingId(null);
    }
  }

  const isLinking = linkingId !== null;
  const hasSearchTerm = searchTerm.trim() !== "";
  const isTruncated = totalMatches > results.length;

  return (
    <Modal isOpen={true} onClose={onClose} title="Link Lawyer" size="medium" blurBackground>
      <div className={styles.body}>
        {/* The search box below is seeded with this text and the user edits it
            away to find anything, so this line is the only place the spreadsheet's
            own words stay readable while they search. Secondary styling, because
            it is context for the search rather than a thing to act on. */}
        <p className={styles.sourceLine}>
          Spreadsheet says: <span className={styles.sourceText}>“{lawyerNameRaw}”</span>
        </p>

        {/* Says out loud what the result list is: this firm's people, not the
            Address Book's. Without it, a user who cannot find someone they know
            exists has no way to tell whether the search is broken or whether the
            person simply works somewhere else. */}
        <p className={styles.scopeLine}>
          Searching contacts at{" "}
          {firmName ? (
            <span className={styles.scopeText}>{firmName}</span>
          ) : (
            "this award's linked firm"
          )}
        </p>

        <input
          className={styles.searchInput}
          type="text"
          value={searchTerm}
          onChange={event => handleSearchChange(event.target.value)}
          placeholder="Search this firm's contacts by name..."
          aria-label="Lawyer name"
        />

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.results}>
          {searching && <p className={styles.hint}>Searching...</p>}
          {/* Deliberately not phrased as a failure. The importer already tried
              this exact name against this exact firm, and tried it hard —
              initial, typo and token-order tiers (awards-api's
              awards-matcher.ts) — which is precisely why this row is on screen.
              Nobody matching is the expected outcome of searching a name the
              Address Book spells differently, and the next move is not a
              different spelling but no spelling at all: the empty box is a real
              search here and lists everyone. */}
          {!searching && hasSearchTerm && results.length === 0 && (
            <p className={styles.hint}>
              Nobody at this firm matches that name. Clear the box to see everyone.
            </p>
          )}
          {/* The empty box already listed everyone, so an empty result from it
              is a different fact entirely: the firm has no contacts at all. */}
          {!searching && !hasSearchTerm && results.length === 0 && (
            <p className={styles.hint}>This firm has no contacts in the Address Book.</p>
          )}
          {!searching && results.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  {/* Job title and office, because within one firm they are what
                      tell two people of the same name apart — and one contact row
                      per office is a normal Address Book shape, so the same person
                      can legitimately appear twice. Email is deliberately absent:
                      it identifies no better than these two and puts a column of
                      personal data on screen for a task that does not need it. */}
                  <th>Job title</th>
                  <th>Office</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {results.map(contact => (
                  <tr key={contact.id}>
                    <td>{contact.name}</td>
                    <td>{contact.jobTitle || "—"}</td>
                    <td>{contact.officeName || "—"}</td>
                    <td>
                      <Button
                        variant="outlined"
                        color="default"
                        size="small"
                        disabled={isLinking}
                        onClick={() => handleLink(contact)}
                      >
                        {linkingId === contact.id ? "Linking..." : "Link"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!searching && isTruncated && (
            <p className={styles.hint}>
              Showing the first {results.length} of {totalMatches} matches — narrow the search to
              see the rest.
            </p>
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={isLinking}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
